import React, { useCallback, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Alert,
  Paper,
  useTheme,
  CircularProgress,
  Tooltip,
  Button,
  Grid,
  Card,
  CardMedia,
  CardActions,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { companyProfileService } from '../../services/companyProfileService';

interface CompanyImageUploaderProps {
  companyId: number;
  value: string[];
  onChange: (imageUrls: string[]) => void;
  disabled?: boolean;
  error?: string;
}

export const CompanyImageUploader: React.FC<CompanyImageUploaderProps> = ({
  companyId,
  value = [],
  onChange,
  disabled = false,
  error,
}) => {
  const theme = useTheme();
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const maxImages = 3;
  const canAddMore = value.length < maxImages;

  const handleFileChange = useCallback(
    async (file: File) => {
      if (!file || disabled) return;

      // ファイル検証
      if (!file.type.startsWith('image/')) {
        setUploadError('画像ファイルのみアップロード可能です');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setUploadError('ファイルサイズは5MB以下にしてください');
        return;
      }

      if (value.length >= maxImages) {
        setUploadError(
          `企業プロフィール画像は最大${maxImages}枚までアップロードできます`
        );
        return;
      }

      setUploading(true);
      setUploadError(null);

      try {
        const response = await companyProfileService.uploadCompanyProfileImage(
          companyId,
          file
        );
        const newImageUrls = [...value, response.data.image_url];
        onChange(newImageUrls);
      } catch (error: any) {
        console.error('画像アップロードエラー:', error);
        const errorMessage =
          error.response?.data?.error?.message ||
          '画像のアップロードに失敗しました';
        setUploadError(errorMessage);
      } finally {
        setUploading(false);
      }
    },
    [companyId, value, onChange, disabled]
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileChange(file);
    }
    // ファイル入力をリセット
    event.target.value = '';
  };

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setDragActive(false);

      if (disabled) return;

      const file = event.dataTransfer.files[0];
      if (file) {
        handleFileChange(file);
      }
    },
    [handleFileChange, disabled]
  );

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    if (!disabled) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setDragActive(false);
  };

  const handleRemove = (indexToRemove: number) => {
    const newImageUrls = value.filter((_, index) => index !== indexToRemove);
    onChange(newImageUrls);
    setUploadError(null);
  };

  const displayError = uploadError || error;

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
        企業プロフィール画像
        <Typography
          component="span"
          variant="body2"
          color="text.secondary"
          sx={{ ml: 1 }}
        >
          （最大{maxImages}枚まで）
        </Typography>
      </Typography>

      {/* 現在の画像一覧 */}
      {value.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={2}>
            {value.map((imageUrl, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card>
                  <CardMedia
                    component="img"
                    height={200}
                    image={imageUrl}
                    alt={`企業画像 ${index + 1}`}
                    sx={{
                      objectFit: 'cover',
                    }}
                  />
                  <CardActions sx={{ justifyContent: 'center', py: 1 }}>
                    <Tooltip title="画像を削除">
                      <IconButton
                        size="small"
                        onClick={() => handleRemove(index)}
                        disabled={disabled}
                        color="error"
                      >
                        <CloseIcon />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* アップロードエリア */}
      {canAddMore && (
        <Paper
          sx={{
            border: 2,
            borderColor: dragActive
              ? theme.palette.primary.main
              : displayError
                ? theme.palette.error.main
                : theme.palette.divider,
            borderStyle: 'dashed',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            backgroundColor: dragActive
              ? theme.palette.action.hover
              : disabled
                ? theme.palette.action.disabledBackground
                : 'transparent',
            transition: 'all 0.2s ease-in-out',
            position: 'relative',
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <input
            type="file"
            accept="image/*"
            onChange={handleInputChange}
            disabled={disabled || uploading}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: 0,
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
          />

          {uploading ? (
            <Box sx={{ py: 2 }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                画像をアップロード中...
              </Typography>
            </Box>
          ) : (
            <Box sx={{ py: 2 }}>
              <AddIcon
                sx={{
                  fontSize: 48,
                  color: disabled
                    ? theme.palette.text.disabled
                    : dragActive
                      ? theme.palette.primary.main
                      : theme.palette.text.secondary,
                  mb: 1,
                }}
              />
              <Typography
                variant="h6"
                color={disabled ? 'text.disabled' : 'text.primary'}
                gutterBottom
              >
                画像を追加
              </Typography>
              <Typography
                variant="body2"
                color={disabled ? 'text.disabled' : 'text.secondary'}
                sx={{ mb: 2 }}
              >
                ファイルをドロップするか、クリックして選択してください
              </Typography>
              <Typography variant="caption" color="text.disabled">
                対応形式: JPG, PNG, GIF（最大5MB）
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* 制限情報 */}
      {value.length >= maxImages && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            最大{maxImages}
            枚の画像がアップロードされています。追加するには既存の画像を削除してください。
          </Typography>
        </Alert>
      )}

      {/* エラー表示 */}
      {displayError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="body2">{displayError}</Typography>
        </Alert>
      )}

      {/* アップロード状況 */}
      {value.length > 0 && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {value.length} / {maxImages} 枚の画像
          </Typography>
        </Box>
      )}
    </Box>
  );
};
